<template>
  <v-container fluid class="stats-page">
    <!-- Filter bar -->
    <v-card class="filter-card mb-6" elevation="0" rounded="lg">
      <v-row no-gutters align="center" class="pa-4">
        <v-col cols="12" sm="auto" class="mr-4 mb-2 mb-sm-0">
          <p class="filter-label">Start Date</p>
          <input class="date-field" type="date" v-model="formattedStartDate" />
        </v-col>

        <v-col cols="12" sm="auto" class="mr-4 mb-2 mb-sm-0">
          <p class="filter-label">End Date</p>
          <input class="date-field" type="date" v-model="formattedEndDate" />
        </v-col>

        <v-col cols="12" sm="auto" class="d-flex align-end mb-2 mb-sm-0" style="gap: 10px;">
          <v-btn
            color="#50568e"
            variant="flat"
            class="text-none action-btn"
            size="small"
            rounded="lg"
            prepend-icon="mdi-magnify"
            @click="submitFilters"
          >
            Search
          </v-btn>

          <v-btn
            variant="outlined"
            class="text-none action-btn"
            color="#8a8a9a"
            rounded="lg"
            prepend-icon="mdi-delete-empty"
            size="small"
            @click="clearFilters"
          >
            Clear filters
          </v-btn>
        </v-col>

        <v-spacer />
      </v-row>
    </v-card>

    <!-- Top summary + chart -->
    <v-row>
      <v-col cols="12" md="4">
        <v-card class="summary-card mb-4" elevation="0" rounded="lg">
          <div class="d-flex align-center pa-5">
            <v-avatar size="54" rounded="lg" class="icon-avatar icon-blue">
              <v-icon color="white" icon="mdi-vanish"></v-icon>
            </v-avatar>
            <div class="ml-4">
              <div class="stats-text">
                {{ stats ? formatNumber(stats.total_campaigns) : "N/A" }}
              </div>
              <span class="campaign-text">Total Campaigns</span>
            </div>
          </div>
        </v-card>

        <v-card class="summary-card mb-4" elevation="0" rounded="lg">
          <div class="d-flex align-center pa-5">
            <v-avatar size="54" rounded="lg" class="icon-avatar icon-teal">
              <v-icon color="white" icon="mdi-tooltip"></v-icon>
            </v-avatar>
            <div class="ml-4">
              <div class="stats-text">
                {{ stats ? formatNumber(stats.total_subscribers) : "N/A" }}
              </div>
              <span class="campaign-text">Total Subscribers</span>
            </div>
          </div>
        </v-card>

        <v-card class="summary-card" elevation="0" rounded="lg">
          <div class="d-flex align-center pa-5">
            <v-avatar size="54" rounded="lg" class="icon-avatar icon-coral">
              <v-icon color="white" icon="mdi-view-sequential"></v-icon>
            </v-avatar>
            <div class="ml-4">
              <div class="stats-text">
                {{ stats ? formatNumber(stats.total_unsubscribers) : "N/A" }}
              </div>
              <span class="campaign-text">Total Unsubscribers</span>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <v-card class="graph-card" variant="flat" rounded="lg" elevation="0">
          <CanvasJSChart :options="options" />
        </v-card>
      </v-col>
    </v-row>

    <!-- Metric strip -->
    <v-row class="mt-2">
      <v-col cols="6" sm="4" md="2" v-for="metric in metricCards" :key="metric.label">
        <v-card class="metric-card d-flex align-center justify-center flex-column" elevation="0" rounded="lg">
          <div class="stats-text">{{ metric.value }}</div>
          <span class="campaign-text">{{ metric.label }}</span>
        </v-card>
      </v-col>
    </v-row>

    <!-- Rate circles -->
    <v-row class="mt-2">
      <v-col cols="6" sm="4" md="2" v-for="rate in rateCards" :key="rate.label">
        <v-card class="rate-card d-flex flex-column justify-center align-center" elevation="0" rounded="lg">
          <v-progress-circular
            :model-value="rate.value"
            :rotate="360"
            :size="80"
            :width="6"
            color="#50568e"
            bg-color="#eceef4"
          >
            <span class="rate-value">{{ rate.value }}%</span>
          </v-progress-circular>
          <div class="text-center mt-3 campaign-text">{{ rate.label }}</div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: "StatsPage",
  beforeRouteEnter(to, from, next) {
    next((v) => {
      const now = new Date();
      const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const formattedLastDay = `${lastDay.getFullYear()}-${String(
        lastDay.getMonth() + 1
      ).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}`;

      v.$store.dispatch("dashboard/getStats", {
        start_date: firstDay,
        end_date: formattedLastDay,
      });
    });
  },

  data() {
    return {
      screenSize: "",
      selectedEndDate: null,
      selectedStartDate: null,
      chart: null,
      formData: {
        start_date: null,
        end_date: null,
      },
    };
  },

  computed: {
    stats() {
      return this.$store.getters["dashboard/dashboardGetters"]("stats");
    },
    formattedStartDate: {
      get() {
        return this.selectedStartDate;
      },
      set(value) {
        this.selectedStartDate = this.formatDate(value);
      },
    },
    formattedEndDate: {
      get() {
        return this.selectedEndDate;
      },
      set(value) {
        this.selectedEndDate = this.formatDate(value);
      },
    },
    metricCards() {
      const s = this.stats;
      return [
        { label: "Total sent", value: s ? this.formatNumber(s.total_number_sent) : "N/A" },
        { label: "Total deliveries", value: s ? this.formatNumber(s.total_number_delivered) : "N/A" },
        { label: "Total opens", value: s ? this.formatNumber(s.total_number_opened) : "N/A" },
        { label: "Total clicks", value: s ? this.formatNumber(s.total_number_clicked) : "N/A" },
        { label: "Total bounces", value: s ? this.formatNumber(s.total_bounces) : "N/A" },
        { label: "Total unsubscribers", value: s ? this.formatNumber(s.total_unsubscribers) : "N/A" },
      ];
    },
    rateCards() {
      const s = this.stats;
      return [
        { label: "Sending rate", value: s?.sending_rate ?? 0 },
        { label: "Delivery rate", value: s?.delivery_rate ?? 0 },
        { label: "Open rate", value: s?.average_open_rate ?? 0 },
        { label: "Click rate", value: s?.average_click_rate ?? 0 },
        { label: "Bounce rate", value: s?.average_bounce_rate ?? 0 },
        { label: "Unsubscribe rate", value: s?.unsubscribe_rate ?? 0 },
      ];
    },
    options() {
      const s = this.stats || {};
      return {
        animationEnabled: true,
        exportEnabled: true,
        theme: "light2",
        title: {
          text: "Email Campaign Performance",
          fontFamily: "Arial",
          fontSize: 20,
          fontWeight: "bold",
          fontColor: "#2d2a4a",
        },
        subtitles: [{ text: "Campaign Metrics Overview", fontSize: 14, fontColor: "#8a8a9a" }],
        axisX: { gridThickness: 0, lineThickness: 1, labelFontFamily: "Arial", labelFontSize: 12 },
        axisY: {
          gridThickness: 0,
          lineThickness: 1,
          labelFontFamily: "Arial",
          labelFontSize: 12,
          title: "Values",
          titleFontFamily: "Arial",
          titleFontSize: 14,
        },
        data: [
          {
            type: "bar",
            indexLabel: "{y}",
            indexLabelFontFamily: "Arial",
            indexLabelFontSize: 12,
            indexLabelPlacement: "outside",
            indexLabelFontColor: "#555",
            dataPoints: [
              { label: "Total Sent", y: s.total_number_sent, color: "#3498db" },
              { label: "Total Delivered", y: s.total_number_delivered, color: "#2ecc71" },
              { label: "Total Opened", y: s.total_number_opened, color: "#9b59b6" },
              { label: "Total Clicked", y: s.total_number_clicked, color: "#e74c3c" },
              { label: "Avg Bounce Rate", y: s.average_bounce_rate, color: "#f39c12", indexLabel: `${s.average_bounce_rate}%` },
              { label: "Avg Open Rate", y: s.average_open_rate, color: "#1abc9c", indexLabel: `${s.average_open_rate}%` },
              { label: "Avg Click Rate", y: s.average_click_rate, color: "#50568e", indexLabel: `${s.average_click_rate}%` },
            ],
          },
        ],
        toolTip: {
          shared: false,
          contentFormatter: (e) => `${e.entries[0].dataPoint.label}: ${e.entries[0].dataPoint.y}`,
        },
      };
    },
  },

  mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
    this.getCurrentDate();
  },

  methods: {
    getCurrentDate() {
      const now = new Date();
      const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const formattedLastDay = `${lastDay.getFullYear()}-${String(
        lastDay.getMonth() + 1
      ).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}`;
      this.selectedStartDate = firstDay;
      this.selectedEndDate = formattedLastDay;
    },
    formatNumber(value) {
      return value !== undefined && value !== null ? new Intl.NumberFormat().format(value) : "N/A";
    },
    submitFilters() {
      this.formData.start_date = this.formattedStartDate;
      this.formData.end_date = this.formattedEndDate;
      this.$store.dispatch("dashboard/getStats", this.formData);
    },
    clearFilters() {
      this.formattedStartDate = null;
      this.formattedEndDate = null;
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toISOString().split("T")[0];
    },
    checkScreenSize() {
      const isSmallScreen = window.matchMedia("(max-width: 768px)").matches;
      this.screenSize = isSmallScreen ? "Small" : "Large";
    },
  },
};
</script>

<style scoped>
.stats-page {
  background: #f5f6fa;
  min-height: 100vh;
  padding: 24px;
}

.filter-card,
.summary-card,
.metric-card,
.rate-card,
.graph-card {
  background: #fff;
  box-shadow: 0 4px 16px -8px rgba(45, 42, 74, 0.08) !important;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.summary-card:hover,
.metric-card:hover,
.rate-card:hover {
  box-shadow: 0 8px 24px -8px rgba(45, 42, 74, 0.16) !important;
  transform: translateY(-2px);
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b6b80;
  margin-bottom: 4px;
}

.date-field {
  width: 160px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #dcdce4;
  padding: 6px 10px;
  font-size: 13px;
  color: #2d2a4a;
  background: #fbfbfd;
}

.date-field:focus {
  outline: none;
  border-color: #50568e;
}

.action-btn {
  height: 40px;
}

.icon-avatar {
  box-shadow: 0 6px 16px -4px rgba(0, 0, 0, 0.25);
}

.icon-blue {
  background: linear-gradient(135deg, #50568e, #6b70b3);
}
.icon-teal {
  background: linear-gradient(135deg, #14b8a6, #0d9488);
}
.icon-coral {
  background: linear-gradient(135deg, #f4756b, #e2564b);
}

.stats-text {
  font-size: 24px;
  font-weight: 700;
  color: #2d2a4a;
  line-height: 1.2;
}

.campaign-text {
  font-size: 12px;
  font-weight: 600;
  color: #8a8a9a;
  letter-spacing: 0.2px;
}

.metric-card {
  height: 100px;
  padding: 8px;
}

.rate-card {
  height: 170px;
  padding: 16px;
}

.rate-value {
  font-size: 14px;
  font-weight: 700;
  color: #2d2a4a;
}

.graph-card {
  padding: 16px;
  min-height: 402px;
}
</style>